/**
 * BPGuidanceModal - Display educational guidance for BP readings
 *
 * Features:
 * - Displays guidance message based on BP category
 * - Color-coded severity levels (success, info, warning, alert)
 * - Dismissible modal (non-blocking)
 * - Gentle, educational tone
 */

import React from 'react';

interface BPGuidance {
  message: string;
  severity: 'success' | 'info' | 'warning' | 'alert';
}

interface BPGuidanceModalProps {
  guidance: BPGuidance | null;
  onClose: () => void;
}

const BPGuidanceModal: React.FC<BPGuidanceModalProps> = ({ guidance, onClose }) => {
  if (!guidance) return null;

  // Severity-based styling
  const severityStyles = {
    success: {
      bg: 'bg-green-50',
      border: 'border-green-400',
      text: 'text-green-800',
      icon: '✓',
      iconBg: 'bg-green-100',
      iconText: 'text-green-600'
    },
    info: {
      bg: 'bg-blue-50',
      border: 'border-blue-400',
      text: 'text-blue-800',
      icon: 'ℹ',
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600'
    },
    warning: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-400',
      text: 'text-yellow-800',
      icon: '⚠',
      iconBg: 'bg-yellow-100',
      iconText: 'text-yellow-600'
    },
    alert: {
      bg: 'bg-red-50',
      border: 'border-red-400',
      text: 'text-red-800',
      icon: '!',
      iconBg: 'bg-red-100',
      iconText: 'text-red-600'
    }
  };

  const style = severityStyles[guidance.severity];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className={`card max-w-lg w-full ${style.bg} border-l-4 ${style.border}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${style.iconBg}`}>
              <span className={`text-2xl ${style.iconText}`}>{style.icon}</span>
            </div>
            <h3 className={`text-xl font-serif font-semibold ${style.text}`}>
              About Your Reading
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
            aria-label="Close guidance"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Message */}
        <div className={`mb-6 ${style.text}`}>
          <p className="text-sm leading-relaxed">{guidance.message}</p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="btn btn-primary"
          >
            Got it
          </button>
        </div>

        {/* Disclaimer for high-severity readings */}
        {(guidance.severity === 'alert' || guidance.severity === 'warning') && (
          <div className="mt-4 pt-4 border-t border-neutral-200">
            <p className="text-xs text-neutral-500 italic">
              Note: This guidance is educational only and not medical advice.
              Always consult your healthcare provider for medical concerns.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BPGuidanceModal;
